import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import Button from "../common/Button";
import { PulseLoader } from "react-spinners";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SectionFooter from "./SectionFooter";
import SoilReportItem from "./SoilReportItem";
import CreateSoilReportDialog from "./CreateSoilReportDialog";
import useFetch from "../../hooks/useFetch";
import { LOCAL_PREFIX } from "../../utils/constants";
import { areSamplesEqual } from "./utils/checkEquality";
import { validateSoil } from "./useReportValidation";
import toast from "react-hot-toast";
import request from "../../utils/fetch";
import { MAX_SOIL_LAYERS } from "./SoilLayerTable";
import toISOString from "./utils/toISOString";
import ProblemIndicator from "./components/ProblemIndicator";
import { Tooltip } from "react-tooltip";

const SoilReport = forwardRef(({
  advanceTab,
  advanceLabel,
  tract,
  tractAcres,
  reportId,
  data,
  refreshData,
  reportYear,
  initialProblems,
}, ref) => {
  const localIdTracker = useRef(0);

  const [saveStatus, setSaveStatus] = useState("clean");

  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const {
    data: knownSampleLocations, refresh: refreshKnownSampleLocations,
  } = useFetch(`/tracts/${tract._id}/soils`);

  const [samples, setSamples] = useState(undefined);
  const dirtySamples = useRef({});

  const [formProblems, setFormProblems] = useState(initialProblems);

  useEffect(() => {
    const remoteSamples = data?.reduce((acc, s) => {
      acc[s._id] = {
        id: s._id,
        name: s.sample_id,
        locationId: s.soil_location_id,
        acres: s.acres_represented ?? "",
        isComposite: !!s.composite_sample,
        labDate: s.lab_date ? new Date(s.lab_date) : "",
        sampleDate: s.sample_date ? new Date(s.sample_date) : "",
        lab: s.lab_name ?? "",
        note: s.note ?? "",
        texture: s.soil_texture ?? "",
        layers: s.soil_layers?.map((l) => {
          return ({
            id: `remote_layer_${l.layer_num}`,
            layerNumber: l.layer_num,
            top: l.top_depth ?? "",
            bottom: l.bottom_depth ?? "",
            calcium: l.calcium ?? "",
            cations: l.cec ?? "",
            magnesium: l.magnesium ?? "",
            nitrate: l.nitrate ?? "",
            organicMatter: l.organic_matter ?? "",
            ph: l.ph ?? "",
            phosphate: l.phosphate ?? "",
            potassium: l.potassium ?? "",
            sodium: l.sodium ?? "",
            solubleSalts: l.soluble_salts ?? "",
            sulfate: l.sulfate ?? "",
          });
        }) ?? [],
      };

      return acc;
    }, {}) ?? {};

    setSamples((og) => {
      og ??= {};

      const samplesToRemove = Object.keys(og).filter((s) => !s.startsWith(LOCAL_PREFIX));

      Object.keys(remoteSamples).forEach((key) => {
        // Don't overwrite any crops that haven't successfully saved.
        if (!dirtySamples.current[key]) {
          og[key] = remoteSamples[key];
        }

        const index = samplesToRemove.indexOf(key);
        if (index >= 0) {
          samplesToRemove.splice(index, 1);
        }
      });

      samplesToRemove.forEach((k) => delete og[k]);

      return { ...og };
    });
  }, [data]);

  const checkDirty = useCallback((changed) => {
    if (!samples) return;

    const original = samples[changed.id];
    if (original && changed && areSamplesEqual(original, changed)) {
      delete dirtySamples.current[changed.id];

      if (Object.keys(dirtySamples.current).length === 0) {
        setSaveStatus("clean");
      } else {
        setSaveStatus("dirty");
      }
    } else {
      dirtySamples.current[changed.id] = changed;
      setSaveStatus("dirty");
    }
  }, [samples]);

  useImperativeHandle(ref, () => ({ isDirty: saveStatus === "dirty" }), [saveStatus]);

  const handleSubmit = useCallback(() => {
    setSaveStatus("saving");
    // Convert the form data back into server-recognizable data.
    const toSave = Object.keys(dirtySamples.current).map((id) => {
      const exists = !id.startsWith(LOCAL_PREFIX);
      const originalLayerCount = samples[id]?.layers?.length ?? 0;

      const sample = dirtySamples.current[id];

      if (sample === "delete") return { id, deleted: true };

      const sampleData = {
        _id: id,
        sample_id: sample.name,
        soil_location_id: sample.locationId,
        acres_represented: sample.acres,
        soil_texture: sample.texture,
        composite_sample: sample.isComposite,
        lab_date: (sample.labDate ?? "") === "" ? sample.labDate : toISOString(sample.labDate),
        sample_date: (sample.sampleDate ?? "") === "" ? sample.sampleDate : toISOString(sample.sampleDate),
        lab_name: sample.lab,
        note: sample.note,
      };

      const mapLayer = (layer, index) => {
        return {
          layer_num: index + 1,
          top_depth: layer.top,
          bottom_depth: layer.bottom,
          calcium: layer.calcium,
          cec: layer.cations,
          magnesium: layer.magnesium,
          nitrate: layer.nitrate,
          organic_matter: layer.organicMatter,
          ph: layer.ph,
          phosphate: layer.phosphate,
          potassium: layer.potassium,
          sodium: layer.sodium,
          soluble_salts: layer.solubleSalts,
          sulfate: layer.sulfate,
        };
      };

      const layers = sample.isComposite ? [mapLayer(sample.layers[0], 0)] : sample.layers.map(mapLayer);
      return { exists, id, sampleData, layers, originalLayerCount };
    });

    const toValidate = Object.values(samples ?? {}).flatMap((s) => {
      const foundInDirty = toSave.find((t) => t.id === s.id);

      if (!foundInDirty) {
        const foundInRemote = data.find((d) => d._id === s.id)
        if (foundInRemote) return [foundInRemote];
        return [];
      }

      if (foundInDirty.deleted) return [];

      return [{ ...foundInDirty.sampleData, soil_layers: foundInDirty.layers }];
    });
    // Validate the server-formatted data.
    const problems = validateSoil(toValidate, tractAcres, tract._nrd);

    const stopSubmission = problems.some((v) => v.level === "error");

    setFormProblems(problems);

    if (stopSubmission) {
      setSaveStatus("dirty");
      return;
    }

    const deletePromises = [];
    const upsertPromises = [];

    const samplePromises = toSave.map((d) => {
      if (d.deleted) {
        return request({
          method: "delete",
          url: `/reports/${reportId}/soils/${d.id}`,
        }).then(() => {
          delete samples[d.id];
          delete dirtySamples.current[d.id];
        });
      }

      // The patch errors if it gets soil_location_id, post needs it though.
      if (d.exists) delete d.sampleData.soil_location_id;
      // It doesn't like getting an id.
      delete d.sampleData._id;

      return request({
        method: d.exists ? "patch" : "post",
        data: d.sampleData,
        url: `reports/${reportId}/soils/${d.exists ? d.id : ""}`,
      }).then((result) => {
        const remoteId = d.exists ? d.id : result;

        // We need to handle deletions first, before saving any new layers.
        const deleteSubs = Array.from(Array(MAX_SOIL_LAYERS).keys()).flatMap((index) => {
          if (index >= d.layers?.length && index <= d.originalLayerCount) {
            return [request({
              method: "delete",
              url: `/reports/${reportId}/soils/${remoteId}/layer/${index + 1}`,
            })];
          }

          return [];
        });

        Promise.all(deleteSubs).then(() => {
          const sampleSubs = d.layers.flatMap((layer, index) => {
            // The server doesn't like getting an empty string in place of no value, so delete them.
            Object.keys(layer).forEach((key) => {
              if (layer[key] === "") delete layer[key];
            });

            if (layer) {
              return [request({
                method: d.originalLayerCount > index ? "patch" : "post",
                data: layer,
                // NOTE: Posting doesn't allow specifying a layer. That might have some concurrency edge cases.
                url: `/reports/${reportId}/soils/${remoteId}/layer/${d.originalLayerCount > index ? index + 1 : ""}`,
              })];
            }

            return [];
          });

          Promise.all(sampleSubs).then(() => {
            delete dirtySamples.current[d.id];
          }).finally(() => {
            if (!d.exists) {
              samples[remoteId] = samples[d.id];
              delete samples[d.id];
            }
          });

          upsertPromises.push(...sampleSubs);
        });

        deletePromises.push(...deleteSubs);
      }).catch((e) => {
        // TODO: Make sure there's no edge cases where this doesn't work and requires a dispatch.
        samples[d.id].failedToSave = true;
        // Rethrow this error so the Promise.all still has something to catch.
        throw e;
      });
    });

    Promise.all(samplePromises).then(() => {
      // NOTE: Need to daisy chain these because all the deletes
      //       have to finish before the upserts can even start.
      Promise.all(deletePromises).then(() => {
        Promise.all(upsertPromises).then(() => {
          toast.success("Successfully updated soils report");
          refreshData().then(() => setSaveStatus("saved"));
        }).catch((e) => {
          toast.error(e.response?.data?.message ?? e.message ?? "Something went wrong");
          refreshData().then(() => setSaveStatus("dirty"));
        });
      }).catch((e) => {
        toast.error(e.response?.data?.message ?? e.message ?? "Something went wrong");
        refreshData().then(() => setSaveStatus("dirty"));
      });
    }).catch((e) => {
      console.error(e);
      toast.error(e.response?.data?.message ?? e.message ?? "Something went wrong");
      refreshData().then(() => setSaveStatus("dirty"));
    });
  }, [samples, data]);

  const nonFieldProblems = formProblems.filter((p) => !p.field && !p.hidden);
  const hasFieldErrors = formProblems.some((p) => {
    return p.field && (p.level === "error" || p.displayLevel === "error")
  });

  return (<>
    <div className="w-full flex justify-between">
      <h3 className="text-2xl font-semibold">Soil Samples</h3>
      {samples && (
        <Button className="h-7" onClick={() => setShowCreateDialog(true)}>+ Add Sample</Button>
      )}
    </div>

    <div className="w-full">
      {!samples && <PulseLoader className="flex p-16 self-center justify-center" color="#658245" />}
      <div
        className="overflow-y-auto pt-2 my-2 flex flex-col gap-1"
        style={{
          // Roughly the height that keeps the save button in view on initial page load.
          minHeight: "calc(100lvh - 40rem)",
          // Roughly the height that keeps the save button and tabs in view when scrolled past the map.
          maxHeight: "calc(100lvh - 20rem)",
        }}
      >
        {Object.values(samples ?? {})?.map((sample, index) => {
          return <SoilReportItem
            key={`soil_sample_item_${sample.id}`}
            index={index}
            sample={sample}
            locations={knownSampleLocations}
            checkDirty={checkDirty}
            onRemove={() => {
              setFormProblems(formProblems.filter((p) => {
                return !p.field?.startsWith(`soil-${sample.id}`) && !p.id?.startsWith(`soil-${sample.id}`);
              }));
              setSamples((og) => {
                if (!sample.id.startsWith(LOCAL_PREFIX)) {
                  // For remote samples only, this tells the save button to send the delete request.
                  dirtySamples.current[sample.id] = "delete";
                  if (saveStatus !== "dirty") {
                    setSaveStatus("dirty");
                  }
                } else {
                  // Otherwise, act like this never existed.
                  delete dirtySamples.current[sample.id];
                }
                delete og[sample.id];
                return { ...og };
              });
            }}
            formProblems={formProblems}
            setFormProblems={setFormProblems}
          />;
        })}
        {(!samples || Object.values(samples).length === 0) && <div className="self-center py-4">
          <div className="mb-4">No samples in report</div>
          <Button className="text-lg" onClick={() => setShowCreateDialog(true)}>Add Sample</Button>
        </div>}
      </div>
    </div>
    {nonFieldProblems.map((p, i) => (
      <ProblemIndicator
        key={`soil_form_issue_${i}`}
        level={p.displayLevel ?? p.level}
        message={p.message}
      />
    ))}
    {hasFieldErrors && <div
      key="soil_form_has_field_errors"
      className="text-sm flex gap-0.5 items-center pl-0.5 text-red-600"
    >
      <FontAwesomeIcon icon="circle-exclamation" />
      One or more fields have errors
    </div>}
    <Tooltip id="invalid-sample-tooltip" place="right">
      There are one or more issues with this sample that must be addressed before saving.
    </Tooltip>
    <SectionFooter
      status={saveStatus}
      handleSubmit={handleSubmit}
      advanceTab={advanceTab}
      advanceLabel={advanceLabel}
    />
    <CreateSoilReportDialog
      open={showCreateDialog}
      onClose={(id) => {
        setShowCreateDialog(false);
        if (id) {
          setSamples((og) => {
            og[`${LOCAL_PREFIX}_${localIdTracker.current}`] = {
              id: `${LOCAL_PREFIX}_${localIdTracker.current}`,
              name: `Sample ${reportYear}`,
              locationId: id,
              layers: [],
            };
            localIdTracker.current += 1;

            setSaveStatus("dirty");
            return { ...og };
          });
        }
      }}
      tract={tract}
      locations={knownSampleLocations}
      refreshLocations={refreshKnownSampleLocations}
    />
  </>);
});

export default SoilReport;
